/**
 * The performance monitor's wire (ADR-087 decision 6, PROTOCOL.md 6.12.0): the gateway offers the server half to a
 * page whose account holds `zone.inspect` (`perfOffer`), the page asks for it about once a second while its panel
 * shows (`perfRequest`) and the gateway answers each request from the map server's counters (`perfReport`). All three
 * are text frames: a report reaches only GMs, at most once a second each, and is never a per-tick path.
 */

/**
 * The shortest interval between requests the gateway answers, in milliseconds, which `perfOffer` names.
 */
export const PERF_INTERVAL_MS = 1000;

/**
 * The fewest milliseconds an offer may name.
 */
export const MIN_PERF_INTERVAL_MS = 250;

/**
 * The most milliseconds an offer may name.
 */
export const MAX_PERF_INTERVAL_MS = 60_000;

/**
 * The most `perfRequest` frames a connection may send in a second, on a window of their own; three windows over in a
 * row close 4003.
 */
export const MAX_PERF_REQUESTS_PER_SECOND = 2;

/**
 * Entities counted by kind over a place: characters (lingering ones included), creatures and, of them, those whose
 * spawn definition is awake (ADR-082 decision 13), and things, every other kind.
 */
export interface PerfCounts {
  readonly characters: number;
  readonly creatures: number;
  readonly awake: number;
  readonly things: number;
}

/**
 * One report of the server half (ADR-087 decision 7): the tick's duration over the last minute at the median and the
 * 99th percentile in microseconds; the sessions joined and their downstream bytes over the last second, the median and
 * the largest; the map server's entities and those of the GM's chunk neighbourhood; the other characters in the view the GM
 * was last sent; and that view's entities in tier 1 (full rate) and tier 2 (quarter rate).
 */
export interface PerfReport {
  readonly tickP50Us: number;
  readonly tickP99Us: number;
  readonly clients: number;
  readonly downP50: number;
  readonly downMax: number;
  readonly zone: PerfCounts;
  readonly area: PerfCounts;
  readonly near: number;
  readonly full: number;
  readonly quarter: number;
}
